import api from 'api';
import { get } from 'utils/fetch';
import { useState, useEffect, useContext } from 'react';
import { message, Spin, Input, Button } from 'antd';
import { isPhoneSize } from 'utils/util';
import { GlobalContext } from 'GlobalContext';
import { isAdmin, isSale } from 'utils/util';
import { USDollar } from 'utils/format';
import { ConstructionDetailed } from './ConstructionDetailed';
import { EditOutlined, PlusOutlined } from '@ant-design/icons';

function TableHeader() {
    return (
        <div
            style={{
                display: 'flex',
                borderBottom: '1px solid black',
                width: 'fit-content',
                alignItems: 'stretch',
            }}
        >
            <HeaderCell width={60}>Edit</HeaderCell>
            <HeaderCell width={200}>Start Date</HeaderCell>
            <HeaderCell width={200}>Target Completion Date</HeaderCell>
            <HeaderCell width={200}>Target Finish Date</HeaderCell>
            <HeaderCell width={200}>Revised Finished Date</HeaderCell>
            <HeaderCell width={200}>Actual Finish Date</HeaderCell>
            <HeaderCell width={180}>Budget</HeaderCell>
            <HeaderCell width={250}>Permit Package</HeaderCell>
        </div>
    );
}

function HeaderCell({ width, children }) {
    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column', // stack children vertically
                alignItems: 'center', // align text to the left
                justifyContent: 'center',
                padding: '8px',
                fontWeight: 'bold',
                whiteSpace: 'pre-wrap',
                borderRight: '1px solid #ddd',
                boxSizing: 'border-box',
                width: width ? `${width}px` : 'auto',
                minWidth: width ? `${width}px` : 'auto',
                flex: `0 0 ${width}px`,
                wordBreak: 'break-word',
                height: 'auto',
                alignSelf: 'stretch',
            }}
        >
            {children}
        </div>
    );
}

function Th({ width, children }) {
    return (
        <th
            style={{
                width,
                minWidth: width,
                padding: '8px',
                borderRight: '1px solid #ddd',
                textAlign: 'center',
                fontWeight: 'bold',
                whiteSpace: 'pre-wrap',
                background: '#f9f9f9',
            }}
        >
            {children}
        </th>
    );
}

function Td({ width, children }) {
    return (
        <td
            style={{
                width,
                minWidth: width,
                padding: '8px',
                borderRight: '1px solid #eee',
                textAlign: 'center',
                whiteSpace: 'pre-wrap',
                verticalAlign: 'middle',
            }}
        >
            {children}
        </td>
    );
}

export function ConstructionTable({ constructions }) {
    const { assetManager } = useContext(GlobalContext);
    const { setConstructionId } = assetManager;

    return (
        <div
            style={{
                overflow: 'auto',
                maxHeight: 'calc(100vh - 100px)', // scrollable body
                border: '1px solid #ddd',
                width: '100%',
            }}
        >
            <table
                style={{
                    borderCollapse: 'collapse',
                    width: 'max-content',
                    minWidth: '100%',
                }}
            >
                {/* Sticky header */}
                <thead
                    style={{
                        position: 'sticky',
                        top: 0,
                        background: 'white',
                        zIndex: 2,
                        borderBottom: '2px solid black',
                    }}
                >
                    <tr>
                        <Th width={60}>Edit</Th>
                        <Th width={200}>Start Date</Th>
                        <Th width={200}>Target Completion Date</Th>
                        <Th width={200}>Target Finish Date</Th>
                        <Th width={200}>Revised Finished Date</Th>
                        <Th width={200}>Actual Finish Date</Th>
                        <Th width={180}>Budget</Th>
                        <Th width={250}>Permit Package</Th>
                    </tr>
                </thead>

                <tbody>
                    {constructions
                        .sort((a, b) => {
                            if (a.start_date === null && b.start_date !== null)
                                return 1;
                            if (a.start_date !== null && b.start_date === null)
                                return -1;

                            const dateA = new Date(a.start_date);
                            const dateB = new Date(b.start_date);

                            if (dateA < dateB) return -1;
                            if (dateA > dateB) return 1;
                            return a.id - b.id;
                        })
                        .map((construction) => (
                            <tr key={construction.id}>
                                <Td width={60}>
                                    <Button
                                        type="link"
                                        icon={<EditOutlined />}
                                        onClick={() =>
                                            setConstructionId(construction.id)
                                        }
                                    />
                                </Td>
                                <Td width={200}>{construction.start_date}</Td>
                                <Td width={200}>
                                    {
                                        construction.construction_target_completion_date
                                    }
                                </Td>
                                <Td width={200}>
                                    {construction.target_finish_date}
                                </Td>
                                <Td width={200}>
                                    {construction.revised_finished_date}
                                </Td>
                                <Td width={200}>
                                    {construction.actual_finish_date}
                                </Td>
                                <Td width={180}>
                                    {USDollar.format(construction.budget)}
                                </Td>
                                <Td width={250}>
                                    {(construction?.permit_package ?? []).map(
                                        (url, idx) => (
                                            <div key={idx}>
                                                <a
                                                    href={url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={{
                                                        display: 'block',
                                                        color: 'blue',
                                                        textDecoration:
                                                            'underline',
                                                    }}
                                                >
                                                    {url.split('/').pop()}
                                                </a>
                                            </div>
                                        )
                                    )}
                                </Td>
                            </tr>
                        ))}
                </tbody>
            </table>
        </div>
    );
}

export function ConstructionInfo() {
    const {
        manageAssetId,
        setManageAssetId,
        accessToken,
        assetManager,
        refreshSession,
    } = useContext(GlobalContext);
    const { constructionId, setConstructionId } = assetManager;
    const [constructions, setConstructions] = useState([]);
    const [loading, setLoading] = useState(
        manageAssetId < 0 || constructionId !== 0 ? false : true
    );
    const [messageApi, contextHolder] = message.useMessage();

    const displayErrorMessage = (message) => {
        messageApi.open({
            type: 'error',
            content: message,
        });
    };

    async function fetchConstructions(assetId) {
        await get(
            api.asset.getConstructions(assetId),
            setConstructions,
            (errors) =>
                errors.forEach((err) => {
                    displayErrorMessage(err.message);
                }),
            accessToken
        );
        setLoading(false);
        refreshSession();
    }

    useEffect(() => {
        if (constructionId === 0 && manageAssetId > 0) {
            setLoading(true);
            fetchConstructions(manageAssetId);
        }
    }, [manageAssetId, constructionId]);

    return (
        <>
            {loading && (
                <div style={{ marginTop: '30px', textAlign: 'center' }}>
                    <Spin />
                </div>
            )}

            {!loading && (
                <>
                    {constructionId !== 0 && (
                        <div
                            style={
                                isPhoneSize()
                                    ? {
                                          height: 'calc(100vh - 310px)',
                                          overflowY: 'scroll',
                                          scrollbarWidth: 'none',
                                          overflowX: 'hidden',
                                      }
                                    : {
                                          height: 'calc(100vh - 30px)',
                                          overflowY: 'scroll',
                                          scrollbarWidth: 'none',
                                          overflowX: 'hidden',
                                      }
                            }
                        >
                            <ConstructionDetailed />
                        </div>
                    )}
                    {constructionId === 0 && (
                        <>
                            {isAdmin() && (
                                <div style={{ marginTop: '10px' }}>
                                    <Button
                                        type="default"
                                        icon={<PlusOutlined />}
                                        onClick={() => setConstructionId(-1)}
                                    >
                                        Add
                                    </Button>
                                </div>
                            )}
                            <ConstructionTable constructions={constructions} />
                        </>
                    )}
                </>
            )}
        </>
    );
}
